import type { ExpandedState } from '@tanstack/react-table';
import { PlusIcon } from 'lucide-react';
import { forwardRef, ReactNode, useMemo, useState } from 'react';

import { FulfillmentStrategy, type SkuDTO } from '@invenco/products-interface';

import { Button } from '@/components/core/button';
import { Card, CardHeader } from '@/components/core/card';
import { EditableDataTable, useEditableRowDataTable } from '@/components/data-table';
import { DataTableCardBody, DataTableCardTitle } from '@/components/data-table/data-table-card';
import { SkuCombobox } from '@/pages/seller/components/sku-combobox';
import type { Result } from '@/shared/helpers/Result';

import { getOrderLineColumns } from './order-line-columns';
import { OrderLineExpandedDetails } from './order-line-expanded-details';
import {
  getDiscountFields,
  getOrderLineTotal,
  isOrderLineManaged,
  orderLineEditableSchema,
  type OrderLine,
  type OrderLineAddInput,
  type OrderLineEditableValues,
  type OrderLineRow,
  type OrderLinesOrderContext,
  type OrderLineUpdatePatch,
} from './order-line-row';
import { ShowMoreOrderLinesButton } from './show-more-order-lines-button';
import { useCardOrderLines } from './use-card-order-lines';

const DRAFT_ORDER_LINE_ID = 'new-order-line';

type EditableOrderLinesCardProps = {
  title: string;
  lines: OrderLine[];
  order: OrderLinesOrderContext;
  isLoading?: boolean;
  isLoadingWithStaleData?: boolean;
  hasError?: boolean;
  emptyMessage?: ReactNode;
  initialVisibleLineCount?: number;
  onAddLine: (line: OrderLineAddInput) => Promise<void | Result<unknown>>;
  onUpdateLine: (lineId: string, patch: OrderLineUpdatePatch) => Promise<void | Result<unknown>>;
  onDeleteLine?: (lineId: string) => Promise<void | Result<unknown>>;
  onAssignSku?: (line: OrderLineRow, sku: SkuDTO) => Promise<void | Result<unknown>>;
};

function buildDraftLine(id: string, sku: SkuDTO): OrderLineRow {
  const line = {
    id,
    skuId: sku.id,
    skuName: sku.name,
    fulfillmentStrategy: sku.fulfillmentStrategy,
    description: sku.description ?? '',
    unitPrice: sku.sellPrice ?? undefined,
    discount: '0%',
    discountRate: 0,
    discountAmount: 0,
  };

  return { ...line, lineTotal: getOrderLineTotal(line) };
}

function buildAddPayload(line: OrderLineRow, values: OrderLineEditableValues): OrderLineAddInput {
  return {
    skuName: line.skuName,
    fulfillmentStrategy: line.fulfillmentStrategy,
    description: values.description,
    qtyOrdered: values.qtyOrdered,
    unitPrice: values.unitPrice,
    ...getDiscountFields(values.discount),
  };
}

function buildUpdatePayload(values: OrderLineEditableValues): OrderLineUpdatePatch {
  return {
    description: values.description,
    qtyOrdered: values.qtyOrdered,
    unitPrice: values.unitPrice,
    ...getDiscountFields(values.discount),
  };
}

export const EditableOrderLinesCard = forwardRef<HTMLElement, EditableOrderLinesCardProps>(
  (
    {
      title,
      lines,
      order,
      isLoading,
      isLoadingWithStaleData,
      hasError,
      emptyMessage = 'No items found',
      initialVisibleLineCount,
      onAddLine,
      onUpdateLine,
      onDeleteLine,
      onAssignSku,
    },
    ref,
  ) => {
    const [isAdding, setIsAdding] = useState(false);
    const [expanded, setExpanded] = useState<ExpandedState>({});
    const [draftLine, setDraftLine] = useState<OrderLineRow | null>(null);
    const {
      visibleLines,
      renderedLines: visibleRenderedLines,
      hiddenLineCount,
      isLimitingLines,
      showAllLines,
    } = useCardOrderLines({
      lines,
      initialVisibleLineCount,
    });
    const renderedLines = useMemo(
      () => (draftLine ? [...visibleRenderedLines, draftLine] : visibleRenderedLines),
      [draftLine, visibleRenderedLines],
    );
    const reserveTypeIconSpace = renderedLines.some(
      (line) => line.fulfillmentStrategy === FulfillmentStrategy.BUNDLE,
    );
    const shouldShowSkuCombobox = isAdding || renderedLines.length === 0;
    const columns = useMemo(
      () => getOrderLineColumns({ orderCurrency: order.currency, reserveTypeIconSpace }),
      [order.currency, reserveTypeIconSpace],
    );

    const {
      table,
      editingRowId,
      startEditingRow,
      onClickRow,
      onCancelEdit,
      onDelete,
      ...editableDataTableProps
    } = useEditableRowDataTable({
      data: renderedLines,
      columns,
      schema: orderLineEditableSchema,
      isLoading,
      isLoadingWithStaleData,
      hasError,
      getSubRows: (row) => row.components,
      getRowCanExpand: (row) => !row.original.assemblyOrderLineId,
      // This card limits rows via show-more rather than pagination controls, so opt out of client
      // pagination — otherwise TanStack's default 10-row page size silently hides rows (including
      // the draft row).
      manualPagination: true,
      manualSorting: false,
      onSaveRow: async (line, values) => {
        if (draftLine?.id === line.id) {
          const result = await onAddLine(buildAddPayload(line, values));
          if (result?.isFailure) return result;
          setDraftLine(null);
          return result;
        }

        return onUpdateLine(line.id, buildUpdatePayload(values));
      },
      onDeleteRow: onDeleteLine ? (line) => onDeleteLine(line.id) : undefined,
      getCanEditRow: (row) =>
        !row.original.assemblyOrderLineId && !isOrderLineManaged(row.original),
      state: { expanded },
      onExpandedChange: setExpanded,
    });

    const handleClickRow: typeof onClickRow = (row, event) => {
      setIsAdding(false);

      if (draftLine?.id === editingRowId && row.id !== draftLine.id) {
        setDraftLine(null);
      }

      if (isOrderLineManaged(row.original)) {
        if (editingRowId && row.id !== editingRowId) {
          onCancelEdit();
        }

        row.toggleExpanded();
        return;
      }

      onClickRow(row, event);
    };

    function handleSelectSku(sku: SkuDTO) {
      const nextDraftLine = buildDraftLine(DRAFT_ORDER_LINE_ID, sku);

      setDraftLine(nextDraftLine);
      setIsAdding(false);
      startEditingRow(nextDraftLine.id, nextDraftLine, { columnId: 'qtyOrdered' });
    }

    function handleClickAdd() {
      if (editingRowId) {
        if (draftLine?.id === editingRowId) {
          setDraftLine(null);
        }

        onCancelEdit();
      }

      setIsAdding(true);
    }

    function handleCancelEdit() {
      if (draftLine && editingRowId === draftLine.id) {
        setDraftLine(null);
        setIsAdding(false);
      }

      onCancelEdit();
    }

    return (
      <Card ref={ref} aria-label={title}>
        <CardHeader>
          <DataTableCardTitle table={table} rowCount={visibleLines.length}>
            {title}
          </DataTableCardTitle>
        </CardHeader>
        <DataTableCardBody table={table} emptyMessage={emptyMessage}>
          <EditableDataTable
            table={table}
            editingRowId={editingRowId}
            {...editableDataTableProps}
            onDelete={editingRowId === draftLine?.id ? undefined : onDelete}
            getRowCanClick={(row) => !row.original.assemblyOrderLineId}
            onClickRow={handleClickRow}
            onCancelEdit={handleCancelEdit}
            renderExpandedRow={(row) => (
              <OrderLineExpandedDetails
                line={row.original}
                channelId={order.channelId ?? undefined}
                editing={!isOrderLineManaged(row.original)}
                onAssignSku={onAssignSku}
              />
            )}
          />
        </DataTableCardBody>
        {isLimitingLines ? (
          <ShowMoreOrderLinesButton hiddenLineCount={hiddenLineCount} onClick={showAllLines} />
        ) : null}
        <div className="flex h-10 items-stretch border-t [[data-slot=card-header]+&]:border-t-0">
          {shouldShowSkuCombobox ? (
            <SkuCombobox
              className="h-full w-full rounded-none border-0 px-2 shadow-none has-[[data-slot=input-group-control]:focus-visible]:border-0 has-[[data-slot=input-group-control]:focus-visible]:ring-0"
              queryParams={{ archived: false }}
              onSelect={handleSelectSku}
              onCancel={() => setIsAdding(false)}
            />
          ) : (
            <Button
              type="button"
              variant="outline"
              className="h-full w-full justify-start rounded-none border-0 px-4 shadow-none"
              onClick={handleClickAdd}
            >
              <PlusIcon />
              Add new item
            </Button>
          )}
        </div>
      </Card>
    );
  },
);
